import React, { forwardRef, memo, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { bem } from 'helpers/class-name';

import './Tooltip.scss';

const { element } = bem('tooltip');

const UnwrappedTooltipPortal = ({ children, id }) => {
  const node = useRef(document.createElement('div'));
  const [dynamic] = useState(!node.current.parentElement);
  useEffect(() => {
    const currentNode = node.current;
    if (dynamic && currentNode) {
      currentNode.id = id;
      document.body.appendChild(node.current);
    }
    return () => {
      if (currentNode?.parentElement?.removeChild && dynamic) {
        currentNode.parentElement.removeChild(currentNode);
      }
    };
  }, [id, dynamic]);
  return createPortal(children, node.current);
};

const TooltipPortal = memo(UnwrappedTooltipPortal);

const TOOLTIP_HOVER_DELAY = 250;

// this needs to be an anchor - https://github.com/classpass/partner-frontend/pull/958
export const TooltipToggle = forwardRef(({ name, className, children, ...restProps }, ref) => (
  <a
    href="#"
    onClick={e => {
      e.preventDefault();
      e.stopPropagation();
    }}
    data-tip
    data-for={name}
    className={element('toggle', className)}
    ref={ref}
    {...restProps}>
    {children}
  </a>
));

TooltipToggle.defaultProps = {};

TooltipToggle.propTypes = {
  name: PropTypes.string.isRequired,
  className: PropTypes.string,
  children: PropTypes.node,
};

TooltipToggle.displayName = 'TooltipToggle';

export const TooltipContent = ({ name, hoverable, place, className, children, idempotent, ...restProps }) => {
  const classes = element('content', className);

  if (idempotent && document.getElementById(name)) {
    return null;
  }

  return (
    <TooltipPortal>
      <ReactTooltip
        {...restProps}
        id={name}
        className={classes}
        place={place}
        type="light"
        effect="solid"
        border={false}
        delayHide={hoverable ? TOOLTIP_HOVER_DELAY : undefined}
        delayUpdate={hoverable ? TOOLTIP_HOVER_DELAY : undefined}>
        <div className={element('scroll-box')}>{children}</div>
      </ReactTooltip>
    </TooltipPortal>
  );
};

TooltipContent.defaultProps = {
  place: 'top',
  hoverable: false,
  idempotent: false,
};

TooltipContent.propTypes = {
  name: PropTypes.string.isRequired,
  hoverable: PropTypes.bool,
  place: PropTypes.string,
  className: PropTypes.string,
  children: PropTypes.node,
  idempotent: PropTypes.bool,
};
